/**
 * 請求一覧画面。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6
 *
 * - status の手動変更 (unpaid → paid → onInvoicePaid Trigger で PO 自動起票)
 * UI: イベント管理画面と統一したカード形式リスト
 */
import type { InvoiceStatus } from '@goenhub/shared';
import { CheckCircle, Copy, ExternalLink, Receipt } from 'lucide-react';
import { useMemo } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useDeals } from '@/hooks/useDeals';
import { useInvoices, useUpdateInvoiceStatus } from '@/hooks/useSalesOps';
import { paths } from '@/routes/paths';

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  unpaid: '未入金',
  paid: '入金済',
  overdue: '期限超過',
  cancelled: 'キャンセル',
};

const STATUS_VARIANT: Record<InvoiceStatus, 'draft' | 'sent' | 'danger' | 'default'> = {
  unpaid: 'draft',
  paid: 'sent',
  overdue: 'danger',
  cancelled: 'default',
};

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

export function InvoicesListPage() {
  const { data: invoices, isLoading } = useInvoices();
  const { data: deals } = useDeals();
  const updateStatus = useUpdateInvoiceStatus();

  const dealMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of deals ?? []) m.set(d.id, d.title);
    return m;
  }, [deals]);

  const handleMarkPaid = async (dealId: string, invoiceId: string) => {
    try {
      await updateStatus.mutateAsync({
        dealId,
        invoiceId,
        status: 'paid',
        paymentMethod: 'transfer',
      });
      toast.success('入金確定: 発注書が自動起票されます');
    } catch (err) {
      toast.error(`更新失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const publicInvoiceUrlFor = (token: string) =>
    `${window.location.origin}${paths.publicInvoice.replace(':token', token)}`;

  return (
    <Screen
      title="請求管理"
      description="入金確定をすると onInvoicePaid Trigger が発火し、発注書が自動起票されます。"
    >
      {isLoading ? (
        <Spinner />
      ) : !invoices || invoices.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="請求書はまだありません"
          description="商談を成約 (Won) すると invoices が自動起票されます。"
        />
      ) : (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {invoices.map((iv) => (
            <div
              key={iv.id}
              className="flex items-center gap-4 p-4 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
            >
              <Receipt className="size-4 text-[var(--text-muted)] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-[13px] font-semibold truncate">
                    {iv.invoiceNumber}
                  </span>
                  <Badge variant={STATUS_VARIANT[iv.status]}>
                    {STATUS_LABEL[iv.status]}
                  </Badge>
                </div>
                <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 flex-wrap">
                  <span>{dealMap.get(iv.dealId) ?? iv.dealId}</span>
                  <span className="tabular-nums">
                    ¥{iv.amount.toLocaleString()}
                  </span>
                  <span>発行: {fmtDate(iv.issuedAt)}</span>
                  <span>期限: {fmtDate(iv.dueDate)}</span>
                  {iv.quoteId && (
                    <code
                      className="font-mono bg-[var(--bg-hover)] px-1.5 py-0.5 rounded text-[10px]"
                      title={`派生元見積: ${iv.quoteId}`}
                    >
                      見積 {iv.quoteId.slice(0, 6)}
                    </code>
                  )}
                </div>
              </div>
              {iv.publicToken && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(publicInvoiceUrlFor(iv.publicToken!));
                      toast.success('公開URLをコピーしました');
                    }}
                    title="公開URLをコピー"
                  >
                    <Copy />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      window.open(publicInvoiceUrlFor(iv.publicToken!), '_blank')
                    }
                    title="印刷ページを開く"
                  >
                    <ExternalLink />
                  </Button>
                </>
              )}
              {iv.status === 'unpaid' && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleMarkPaid(iv.dealId, iv.id)}
                  title="入金確定"
                  disabled={updateStatus.isPending}
                >
                  <CheckCircle />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Screen>
  );
}
